import { useRef, useState, useEffect } from 'react'
import { SvgChevronRight } from '../assets/images'

interface CarouselProps<T> {
  items: T[]
  renderItem: (item: T, index: number) => JSX.Element
  scrollPercentage?: number
  showScrolls?: boolean
}

const Carousel = <T,>({
  items,
  renderItem,
  scrollPercentage = 0.8,
  showScrolls = true,
}: CarouselProps<T>) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showLeftButton, setShowLeftButton] = useState(false)
  const [showRightButton, setShowRightButton] = useState(false)

  const [scrollAmount, setScrollAmount] = useState(0)

  const updateScrollAmount = () => {
    if (scrollRef.current) {
      const containerWidth = scrollRef.current.clientWidth
      setScrollAmount(containerWidth * scrollPercentage)
    }
  }

  const updateScrollButtons = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current
      const tolerance = 1

      const isAtStart = scrollLeft <= tolerance
      const isAtEnd = scrollLeft + clientWidth >= scrollWidth - tolerance

      setShowLeftButton(!isAtStart)
      setShowRightButton(!isAtEnd)
    }
  }

  useEffect(() => {
    updateScrollButtons()
    updateScrollAmount()

    const handleScroll = () => updateScrollButtons()
    const handleResize = () => {
      updateScrollAmount()
      updateScrollButtons()
    }

    const scrollContainer = scrollRef.current
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll)
      window.addEventListener('resize', handleResize)
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll)
      }
      window.removeEventListener('resize', handleResize)
    }
  }, [items.length])

  return (
    <div className="flex items-center w-full gap-2">
      {showScrolls && showLeftButton && (
        <button
          onClick={() =>
            scrollRef.current?.scrollBy({
              left: -scrollAmount,
              behavior: 'smooth',
            })
          }
          className="z-10 bg-white shadow rounded-full p-2"
        >
          <SvgChevronRight className="w-5 h-5 transform rotate-180" />
        </button>
      )}

      <div
        ref={scrollRef}
        className="overflow-x-auto flex gap-2 p-2 scrollbar-hidden w-full"
      >
        {items.map((item, index) => renderItem(item, index))}
      </div>

      {showScrolls && showRightButton && (
        <button
          onClick={() =>
            scrollRef.current?.scrollBy({
              left: scrollAmount,
              behavior: 'smooth',
            })
          }
          className="z-10 bg-white rounded-full p-2"
        >
          <SvgChevronRight className="w-5 h-5" />
        </button>
      )}
    </div>
  )
}

export default Carousel
